import React, { useState, useEffect } from 'react';
import { theme } from '../../../theme';
import { useOwnerComplex } from '../../../hooks/venue/useOwnerComplex';
import { apiUtils } from '../../../utils/apiUtils';
import {
  CreditCard,
  ExternalLink,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';

interface MPStatus {
  connected: boolean;
  mode?: 'test' | 'production';
  message?: string;
  requiresOAuth?: boolean;
  mpUserId?: string;
  expiresInDays?: number;
  isExpired?: boolean;
}

const OwnerSettingsPage: React.FC = () => {
  const { complex, loading: loadingComplex, fetchComplex } = useOwnerComplex();
  const [mpStatus, setMpStatus] = useState<MPStatus | null>(null);
  const [loadingMP, setLoadingMP] = useState(true);

  useEffect(() => {
    fetchComplex();
    fetchMPStatus();
  }, []);

  const fetchMPStatus = async () => {
    try {
      setLoadingMP(true);
      const response = await apiUtils.get('/api/mercadopago/status');
      setMpStatus(response.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching MP status:', error);
      setMpStatus({ connected: false });
    } finally {
      setLoadingMP(false);
    }
  };

  const handleConnectMP = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('No estás autenticado. Por favor, iniciá sesión nuevamente.');
      return;
    }

    const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    // Redirigir al endpoint de OAuth con el token como query param
    window.location.href = `${serverUrl}/api/mercadopago/auth/authorize?token=${encodeURIComponent(token)}`;
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    padding: '40px 48px',
    maxWidth: '900px',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '36px',
    fontWeight: 'bold',
    color: theme.colors.text,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    letterSpacing: '1px',
    marginBottom: '8px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '16px',
    color: theme.colors.textSecondary,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: '700',
    color: theme.colors.textSecondary,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    letterSpacing: '0.5px',
    marginBottom: '16px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: '12px',
    padding: '20px',
    border: `1px solid ${theme.colors.border}`,
  };

  const cardHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
  };

  const iconContainerStyle: React.CSSProperties = {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    backgroundColor: '#E3F2FD',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const cardTitleStyle: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: '600',
    color: theme.colors.text,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  };

  const cardDescStyle: React.CSSProperties = {
    fontSize: '14px',
    color: theme.colors.textSecondary,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  };

  const statusContainerStyle = (connected: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    borderRadius: '8px',
    backgroundColor: connected ? '#E8F5E9' : '#FFF3E0',
    marginBottom: '20px',
    width: 'fit-content',
  });

  const statusTextStyle = (connected: boolean): React.CSSProperties => ({
    fontSize: '14px',
    fontWeight: '600',
    color: connected ? '#2E7D32' : '#E65100',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  });

  const connectButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: theme.colors.primary,
    color: theme.colors.background,
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '700',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    letterSpacing: '0.5px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const infoCardStyle: React.CSSProperties = {
    ...cardStyle,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const fieldRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '24px',
  };

  const fieldStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: '600',
    color: theme.colors.text,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  };

  const valueStyle: React.CSSProperties = {
    padding: '12px 16px',
    backgroundColor: theme.colors.background,
    borderRadius: '8px',
    border: `1px solid ${theme.colors.border}`,
    fontSize: '14px',
    color: theme.colors.text,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  };

  const statusBadgeStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#E8F5E9',
    borderRadius: '8px',
    width: 'fit-content',
  };

  const statusDotStyle: React.CSSProperties = {
    width: '8px',
    height: '8px',
    borderRadius: '100px',
    backgroundColor: '#4CAF50',
  };

  const dangerCardStyle: React.CSSProperties = {
    ...cardStyle,
    border: '1px solid #FF1744',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const dangerButtonStyle: React.CSSProperties = {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#FF1744',
    border: '1px solid #FF1744',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  if (loadingComplex || loadingMP) {
    return (
      <div style={containerStyle}>
        <p style={{ color: theme.colors.textSecondary }}>Cargando...</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={titleStyle}>Configuración</h1>
        <p style={subtitleStyle}>
          Gestioná las integraciones y preferencias de tu complejo
        </p>
      </header>

      {/* Integraciones de Pago */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>INTEGRACIONES DE PAGO</h2>
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <div style={iconContainerStyle}>
              <CreditCard size={24} color="#2196F3" />
            </div>
            <div>
              <h3 style={cardTitleStyle}>Mercado Pago</h3>
              <p style={cardDescStyle}>
                Conectá tu cuenta para recibir pagos automáticamente
              </p>
            </div>
          </div>

          <div style={statusContainerStyle(mpStatus?.connected || false)}>
            {mpStatus?.connected ? (
              <>
                <CheckCircle size={20} color="#2E7D32" />
                <span style={statusTextStyle(true)}>
                  {mpStatus.mode === 'test' ? 'Modo Prueba' : 'Conectado'}
                </span>
              </>
            ) : (
              <>
                <AlertCircle size={20} color="#F57C00" />
                <span style={statusTextStyle(false)}>No Conectado</span>
              </>
            )}
          </div>

          {mpStatus?.message && (
            <p style={{ ...cardDescStyle, marginBottom: '16px' }}>
              {mpStatus.message}
            </p>
          )}

          {mpStatus?.requiresOAuth && !mpStatus.connected && (
            <button
              style={connectButtonStyle}
              onClick={handleConnectMP}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 230, 118, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <ExternalLink size={18} />
              CONECTAR CON MERCADO PAGO
            </button>
          )}

          {mpStatus?.mode === 'production' && mpStatus.connected && mpStatus.expiresInDays !== undefined && (
            <p style={{ ...cardDescStyle, marginTop: '8px' }}>
              {mpStatus.isExpired ? (
                <span style={{ color: '#FF1744' }}>Token expirado. Reconectá tu cuenta.</span>
              ) : (
                `Expira en ${mpStatus.expiresInDays} días`
              )}
            </p>
          )}
        </div>
      </section>

      {/* Información del Complejo */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>INFORMACIÓN DEL COMPLEJO</h2>
        <div style={infoCardStyle}>
          <div style={fieldRowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Nombre del Complejo</label>
              <div style={valueStyle}>{complex?.name || 'Sin nombre'}</div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Teléfono</label>
              <div style={valueStyle}>{complex?.phone || 'No configurado'}</div>
            </div>
          </div>

          <div style={fieldRowStyle}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Dirección</label>
              <div style={valueStyle}>{complex?.address || 'No configurada'}</div>
            </div>
            <div style={{ ...fieldStyle, flex: 'none' }}>
              <label style={labelStyle}>Estado</label>
              <div style={statusBadgeStyle}>
                <div style={statusDotStyle} />
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#2E7D32', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                  Online
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zona Peligrosa - TODO: Implementar flujo de solicitud de baja con admin */}
      {/* Por ahora oculto hasta tener el sistema de solicitudes */}
    </div>
  );
};

export default OwnerSettingsPage;
